import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, peso, fecha, hoy } from '../utils/formato';

// La bandeja de apuntes.
//
// «Gabriel pesa los quesos y manda la información al muchacho que llena el
// programa en la oficina. La idea es que él registre esa información, que el
// muchacho la vea, y con eso la ingrese. Lo mismo de los préstamos que le hace
// a una finca o a su trabajador.»
//
// Hoy eso va por WhatsApp: un mensaje entre otros veinte, sin forma de saber
// cuál ya se metió y cuál no.
//
// ── UN APUNTE NO ES UN DATO. ES UN RECADO ──
//
// Gabriel NO registra la cuajada: deja un apunte que dice «pesé 641,30 kg en
// RETÉN». El muchacho es quien registra, igual que hoy — con la pantalla de
// siempre, sus validaciones y sus avisos. Aquí solo se pasa el recado.
//
// Por eso esta pantalla no valida casi nada: validar aquí sería registrar a
// medias, y entonces habría dos sitios donde se decide si una cuajada está
// bien. Lo único que sí exige es la FECHA, porque sin ella el dato termina con
// la del día en que se tecleó.

const CLASES = {
  cuajada:         { ico: '🧀', nombre: 'Pesé queso',                 donde: '🧀 Producción' },
  prestamo_finca:  { ico: '💵', nombre: 'Le presté a una finca',      donde: '🚜 Fincas' },
  vale_trabajador: { ico: '💵', nombre: 'Le presté a un trabajador',  donde: '👷 Nómina' },
  nota:            { ico: '✏️', nombre: 'Otra cosa',                  donde: null },
};

// Cuántos días lleva un apunte sin registrar. Es el aviso que de verdad sirve:
// uno de hace tres días es producción que no está en los libros.
const diasSin = (a) => {
  const d = new Date(String(a.fecha).slice(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((new Date(hoy() + 'T00:00:00') - d) / 86400000));
};

function Apuntes() {
  const [bandeja, setBandeja] = useState(null);
  const [escribiendo, setEscribiendo] = useState(null);   // {tipo, fecha, ...}
  const [guardando, setGuardando] = useState(false);
  const [verTodos, setVerTodos] = useState(false);
  const [error, setError] = useState('');
  // Lo que el programa encontró raro en lo que acaba de escribir. Mientras haya
  // algo aquí, el apunte NO se ha mandado: está esperando que él diga si así es.
  const [avisos, setAvisos] = useState([]);
  // Y que se vea sin tener que buscarlo. Él oprimió «Mandar» estando al pie del
  // formulario; sin esto, el aviso aparece arriba, fuera de la pantalla, y lo
  // único que él nota es que no pasó nada.
  const alAvisar = useCallback((nodo) => {
    if (nodo) nodo.scrollIntoView({ block: 'nearest' });
  }, []);

  // Las listas para los desplegables. Van aquí y no escritas a mano porque
  // Gabriel tiene que poder escoger SU ruta y SU finca, no teclear el nombre:
  // «RETEN» y «RETÉN» son dos rutas distintas para quien después busca.
  const [rutas, setRutas] = useState([]);
  const [fincas, setFincas] = useState([]);
  const [gente, setGente] = useState([]);

  const cargar = useCallback(() => {
    axios.get('/api/apuntes' + (verTodos ? '?todos=1' : ''))
      .then(r => { setBandeja(r.data || null); setError(''); })
      .catch(() => setError('No se pudo cargar. Recargue la página.'));
  }, [verTodos]);

  useEffect(cargar, [cargar]);

  useEffect(() => {
    axios.get('/api/centros').then(r => setRutas(r.data || [])).catch(() => setRutas([]));
    axios.get('/api/fincas').then(r => setFincas(r.data || [])).catch(() => setFincas([]));
    axios.get('/api/trabajadores').then(r => setGente(r.data || [])).catch(() => setGente([]));
  }, []);

  // `confirmado` va en true solo cuando él ya leyó los avisos y dijo que así es.
  //
  // No se manda siempre en true «para no molestar»: el servidor es el que sabe
  // qué es raro, y el día que se le añada una revisión nueva esta pantalla se
  // entera sola.
  const mandar = async (e, confirmado = false) => {
    if (e) e.preventDefault();
    const { tipo, fecha: f, nota, ...datos } = escribiendo;
    setGuardando(true);
    try {
      await axios.post('/api/apuntes',
        { tipo, fecha: f, nota: nota || null, datos, confirmado });
      setEscribiendo(null);
      setAvisos([]);
      cargar();
    } catch (err) {
      const d = err.response?.data;
      // NO SE GUARDÓ TODAVÍA. Hay algo que no cuadra y se le enseña, con el
      // formulario abierto y los números donde los dejó — que es lo único que
      // permite corregirlo sin volver a teclear todo.
      if (err.response?.status === 409 && d?.revisar) {
        setAvisos(d.avisos || []);
      } else {
        alert(d?.error || 'No se pudo mandar el apunte');
      }
    }
    setGuardando(false);
  };

  // Tocar un campo borra los avisos de hace un momento.
  //
  // Si no, él corrige el número, oprime «sí, así es» y estaría confirmando un
  // aviso que hablaba de OTRA cifra — y el nuevo valor se colaría sin revisar,
  // que es justo lo contrario de lo que esto hace.
  const cambiar = (campos) => {
    setEscribiendo(a => ({ ...a, ...campos }));
    if (avisos.length) setAvisos([]);
  };

  const cerrarFormulario = () => { setEscribiendo(null); setAvisos([]); };

  const descartar = async (a) => {
    if (!window.confirm(
      'Descartar este apunte.\n\n' +
      'Úselo cuando se mandó dos veces o quedó mal. No lo use para «ya lo ' +
      'registré»: para eso está el botón de registrar, que deja el rastro.')) return;
    try {
      await axios.delete(`/api/apuntes/${a.id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo descartar');
    }
  };

  const marcar = async (a) => {
    try {
      await axios.post(`/api/apuntes/${a.id}/registrado`, {});
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo marcar');
      cargar();
    }
  };

  if (error) return <div className="aviso malo">{error}</div>;
  if (!bandeja) return <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>;

  const lista = bandeja.apuntes || [];
  const pendientes = lista.filter(a => !a.registrado_en);

  // Lo que el apunte dice, en una línea. Cada clase se lee distinta.
  const enUnaLinea = (a) => {
    const d = a.datos || {};
    if (a.tipo === 'cuajada') {
      return [d.ruta, d.litros ? peso(d.litros, 'L') : null,
              d.kilos ? peso(d.kilos, 'kg') : null,
              d.bufala ? `🐃 ${peso(d.bufala, 'L')}` : null]
        .filter(Boolean).join(' · ');
    }
    if (a.tipo === 'prestamo_finca' || a.tipo === 'vale_trabajador') {
      return [d.a_quien, d.monto ? plata(d.monto) : null].filter(Boolean).join(' · ');
    }
    return '';
  };

  return (
    <>
      <div className="encabezado">
        <h1>Apuntes</h1>
        <p>Lo que se anotó en la planta y todavía no está en el programa</p>
      </div>

      {/* LOS BOTONES DE MANDAR, ARRIBA DEL TODO.
          
          Gabriel entra a esto con el queso recién pesado y el teléfono en la
          mano. Si lo primero que ve es una lista, tiene que buscar dónde
          escribir. */}
      <div className="bloque">
        <h2>✍️ Anotar algo</h2>
        <div className="cuerpo">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {Object.entries(CLASES).map(([tipo, c]) => (
              <button key={tipo} className="boton suave"
                      style={{ flex: '1 1 180px', padding: '14px 16px' }}
                      onClick={() => setEscribiendo({ tipo, fecha: hoy() })}>
                {c.ico} {c.nombre}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bloque">
        <h2>
          📮 {verTodos ? 'Todos los apuntes' : 'Sin registrar'}
          <span className="contador">{lista.length}</span>
          <button className="boton chico suave" style={{ marginLeft: 'auto' }}
                  onClick={() => setVerTodos(!verTodos)}>
            {verTodos ? 'Solo los pendientes' : 'Ver también los registrados'}
          </button>
        </h2>
        <div className="cuerpo">
          {lista.length === 0 ? (
            <div className="vacio">
              {verTodos
                ? 'Todavía no se ha anotado nada.'
                : 'Todo lo anotado ya está registrado en el programa.'}
            </div>
          ) : lista.map(a => {
            const c = CLASES[a.tipo] || CLASES.nota;
            const dias = diasSin(a);
            const viejo = !a.registrado_en && dias >= 2;
            return (
              <div key={a.id} style={{ padding: '12px 0',
                                       borderTop: '1px solid var(--raya)' }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap',
                              alignItems: 'baseline' }}>
                  <strong>{c.ico} {c.nombre}</strong>
                  {/* LA FECHA DEL HECHO, primero y en grande.
                      
                      No la de cuándo se mandó: es la que va a los libros. Una
                      cuajada registrada con la fecha del día en que se tecleó
                      corre el rendimiento de la ruta, el cuadre de la leche y
                      el costo del lote. Ya pasó. */}
                  <span className="pinta gris">del {fecha(a.fecha)}</span>
                  {!a.registrado_en && (
                    <span className={'pinta ' + (viejo ? 'malo' : 'ojo')}
                          style={{ marginLeft: 'auto' }}>
                      {dias === 0 ? 'sin registrar' : `sin registrar hace ${dias} ${dias === 1 ? 'día' : 'días'}`}
                    </span>
                  )}
                  {a.registrado_en && (
                    <span className="pinta bueno" style={{ marginLeft: 'auto' }}>
                      registrado
                    </span>
                  )}
                </div>

                {enUnaLinea(a) && (
                  <div style={{ fontSize: 15, marginTop: 4 }}>{enUnaLinea(a)}</div>
                )}
                {a.nota && (
                  <div style={{ fontSize: 13, marginTop: 3 }}>«{a.nota}»</div>
                )}

                {/* EL APUNTE QUE SALIÓ SEÑALADO.

                    Es la mitad del valor de avisar. El aviso se lo llevaría el
                    viento si muriera en el teléfono de Gabriel: el muchacho
                    recibiría el apunte sin saber que los números no cuadraban y
                    lo registraría a ciegas.

                    Enseñándolo aquí, el que va a teclear sabe cuál de los
                    veinte apuntes es el que hay que mirar dos veces. */}
                {(a.avisos || []).length > 0 && (
                  <div className="aviso malo" style={{ marginTop: 6, fontSize: 13 }}>
                    <strong>Se le avisó y confirmó que así fue:</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {a.avisos.map((x, n) => (
                        <li key={n}>{x.texto}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--tinta-tenue)', marginTop: 4 }}>
                  Mandado el {fecha(a.creado_en)}
                  {a.quien ? ` por ${a.quien}` : ''}
                  {a.registrado_en ? ` · registrado el ${fecha(a.registrado_en)}` : ''}
                </div>

                {!a.registrado_en && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    {/* SE REGISTRA EN LA PANTALLA DE SIEMPRE, no aquí.
                        
                        Un segundo sitio donde meter una cuajada sería un
                        segundo sitio donde equivocarse, y con otras
                        validaciones. Aquí solo se dice dónde va y se marca
                        cuando ya está. */}
                    {c.donde && (
                      <span style={{ fontSize: 13, color: 'var(--tinta-tenue)',
                                     alignSelf: 'center' }}>
                        Se registra en {c.donde} →
                      </span>
                    )}
                    <button className="boton chico" style={{ marginLeft: 'auto' }}
                            onClick={() => marcar(a)}>
                      ✅ Ya lo registré
                    </button>
                    <button className="boton chico suave" onClick={() => descartar(a)}>
                      Descartar
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── EL FORMULARIO DE MANDAR ───────────────────────────────────────
          
          Tres o cuatro campos, nunca más. Gabriel pesa y manda; el formulario
          completo —insumos, mano de obra, lote, rendimiento— es del muchacho. */}
      {escribiendo && (
        <div className="capa">
          <form className="tarjeta" onSubmit={mandar}>
            <h2>
              {CLASES[escribiendo.tipo].ico} {CLASES[escribiendo.tipo].nombre}
            </h2>
            <div className="cuerpo">
              {/* ── LO QUE NO CUADRA ─────────────────────────────────────────

                  VA ARRIBA DEL TODO, y eso no es estética.

                  Puesto al pie del formulario, en el teléfono queda debajo de
                  seis campos: Gabriel oprime «Mandar», no pasa nada donde él
                  está mirando, y vuelve a oprimir. Arriba se lee en el orden en
                  que se piensa — qué no cuadra, los campos para arreglarlo, y
                  al final los dos botones.

                  Sale con el formulario abierto y los números donde los dejó.
                  No es un error: el apunte no se ha mandado y él decide.

                  Esto existe por dos días concretos. El 2 de septiembre RETÉN
                  llevó 366 litros de leche cruda contados dos veces y el
                  promedio se movió sin que nadie lo notara en seis días. El 18
                  de agosto la producción del día entero se registró dos veces y
                  se supo en septiembre, con los lotes ya vendidos.

                  Los dos se preguntan aquí, mientras él tiene la báscula al
                  frente. */}
              {avisos.length > 0 && (
                <div className="aviso malo" ref={alAvisar}
                     style={{ marginBottom: 14 }}>
                  <strong>Revise esto antes de mandarlo</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {avisos.map((a, n) => (
                      <li key={n} style={{ marginTop: 4 }}>{a.texto}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="campos">
                {/* LA FECHA VA PRIMERO Y ES OBLIGATORIA.
                    
                    Arranca en hoy porque es lo más común, pero se puede
                    cambiar: si el sábado se acuerda de que el jueves prestó
                    $200.000, el apunte tiene que ser del jueves. */}
                <label className="campo">
                  <span>¿Qué día se hizo? *</span>
                  <input type="date" required max={hoy()}
                         value={escribiendo.fecha}
                         onChange={e => cambiar({
                           fecha: e.target.value })} />
                </label>

                {escribiendo.tipo === 'cuajada' && (
                  <>
                    <label className="campo">
                      <span>¿En cuál ruta?</span>
                      {/* VAN EL ID Y EL NOMBRE, los dos.

                          El id es con el que el programa va a buscar qué rinde
                          normalmente esta ruta para poder avisar; el nombre es
                          lo que se lee en la bandeja sin tener que consultar
                          nada. Guardando solo el nombre, una ruta que mañana se
                          renombre dejaría los apuntes viejos apuntando a un
                          sitio que ya no existe. */}
                      <select value={escribiendo.ruta_id || ''}
                              onChange={e => {
                                const r = rutas.find(
                                  x => String(x.id) === e.target.value);
                                cambiar({ ruta_id: r ? r.id : null,
                                          ruta: r ? r.nombre : '' });
                              }}>
                        <option value="">— escoja —</option>
                        {rutas.map(r => (
                          <option key={r.id} value={r.id}>{r.nombre}</option>
                        ))}
                      </select>
                    </label>
                    <label className="campo">
                      <span>Litros de leche</span>
                      <input type="number" step="1" min="0" inputMode="decimal"
                             value={escribiendo.litros || ''}
                             onChange={e => cambiar({
                               litros: e.target.value })} />
                    </label>
                    <label className="campo">
                      <span>Kilos de queso *</span>
                      <input type="number" step="0.01" min="0" required
                             inputMode="decimal" autoFocus
                             value={escribiendo.kilos || ''}
                             onChange={e => cambiar({
                               kilos: e.target.value })} />
                    </label>
                    <label className="campo">
                      <span>🐃 De esos litros, ¿cuántos de búfala?</span>
                      <input type="number" step="1" min="0" inputMode="decimal"
                             value={escribiendo.bufala || ''}
                             onChange={e => cambiar({
                               bufala: e.target.value })} />
                    </label>
                  </>
                )}

                {(escribiendo.tipo === 'prestamo_finca' ||
                  escribiendo.tipo === 'vale_trabajador') && (
                  <>
                    <label className="campo">
                      <span>¿A quién? *</span>
                      <select required value={escribiendo.a_quien_id || ''}
                              onChange={e => {
                                const lista = escribiendo.tipo === 'prestamo_finca'
                                  ? fincas : gente;
                                const x = lista.find(
                                  y => String(y.id) === e.target.value);
                                cambiar({ a_quien_id: x ? x.id : null,
                                          a_quien: x ? x.nombre : '' });
                              }}>
                        <option value="">— escoja —</option>
                        {(escribiendo.tipo === 'prestamo_finca' ? fincas : gente)
                          .map(x => (
                            <option key={x.id} value={x.id}>{x.nombre}</option>
                          ))}
                      </select>
                    </label>
                    <label className="campo">
                      <span>¿Cuánto? *</span>
                      <input type="number" step="1000" min="0" required
                             inputMode="numeric" className="peso"
                             value={escribiendo.monto || ''}
                             onChange={e => cambiar({
                               monto: e.target.value })} />
                    </label>
                  </>
                )}
              </div>

              <label className="campo">
                <span>{escribiendo.tipo === 'nota' ? '¿Qué pasó? *' : 'Nota'}</span>
                <input required={escribiendo.tipo === 'nota'}
                       placeholder={escribiendo.tipo === 'nota'
                         ? 'Lo que haya que decirle al muchacho'
                         : 'Para qué era, o lo que quiera aclarar'}
                       value={escribiendo.nota || ''}
                       onChange={e => cambiar({
                         nota: e.target.value })} />
              </label>

              <div className="aviso" style={{ marginTop: 12 }}>
                Esto <strong>no queda registrado todavía</strong>: es un recado
                para quien llena el programa. Él lo mete y lo marca.
              </div>
            </div>
            <div className="acciones">
              <button type="button" className="boton suave"
                      onClick={cerrarFormulario}>Cancelar</button>
              {avisos.length > 0 ? (
                <>
                  {/* «Corregir» es el que va resaltado, y va primero.

                      Casi siempre el aviso tiene razón. El botón de seguir de
                      largo existe —el aviso avisa, no impide— pero no puede ser
                      el camino cómodo, o se vuelve el que se oprime sin leer. */}
                  <button type="button" className="boton"
                          onClick={() => setAvisos([])}>
                    ✏️ Corregir
                  </button>
                  <button type="button" className="boton suave" disabled={guardando}
                          onClick={() => mandar(null, true)}>
                    {guardando ? 'Mandando…' : 'Sí, así fue — mandar'}
                  </button>
                </>
              ) : (
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Mandando…' : '📮 Mandar'}
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default Apuntes;
